import { useEffect, useRef, useState } from 'react';
import { resendVerificationEmail } from '../../services/authService';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import { rarityName } from '../PendingCoinCard/PendingCoinCard';
import styles from './EmailVerificationPrompt.module.css';

const DEFAULT_RESEND_COOLDOWN_SECONDS = 60;

/** Player-facing message for a failed resend. Never shows raw server errors. */
export function resendErrorMessage(err) {
    if (err?.code === 'rate_limit_exceeded') return err.message || 'Too many emails requested. Please try again shortly.';
    if (err?.code === 'verification_email_unavailable') return err.message;
    if (err?.isNetworkError || err?.statusCode === 0) return "You're offline. Check your connection and try again.";
    return "We couldn't send the email. Please try again in a few minutes.";
}

/**
 * Asks a password-based account to verify its email. Nothing is lost: the
 * intended quiz is remembered by the caller and restarted once the server
 * confirms the verification (`onContinue` must ask the server).
 *
 * - default: shown BEFORE question 1 when a quiz cannot start yet.
 * - with `pendingCoin`: the account's one quiz before verification earned a
 *   coin that is held until the email is verified.
 * - `variant="keep_playing"`: the one quiz is used up and earned nothing.
 *
 * Resend: one request at a time, then a cooldown mirroring the server's (the
 * server enforces it either way). `onAlreadyVerified` runs when the server
 * says the account is already verified.
 */
export default function EmailVerificationPrompt({
    email, onContinue, onChangeEmail = null, onNotNow = null, onAlreadyVerified = null,
    continuing = false, error = null, notice = null, context = {},
    pendingCoin = null, variant = null, continueLabel = null,
}) {
    const [resendState, setResendState] = useState({ status: 'idle', message: null });
    const [cooldown, setCooldown] = useState(0);
    const resendInFlightRef = useRef(false);
    const shownRef = useRef(false);

    useEffect(() => {
        if (shownRef.current) return;
        shownRef.current = true;
        trackEvent(ANALYTICS_EVENTS.EMAIL_VERIFICATION_PROMPT_SHOWN, { platform: 'web', ...context });
    }, [context]);

    // A countdown for the button label only; no network requests.
    useEffect(() => {
        if (cooldown <= 0) return undefined;
        const t = setTimeout(() => setCooldown((n) => Math.max(0, n - 1)), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    const handleResend = async () => {
        if (resendInFlightRef.current || cooldown > 0) return;
        resendInFlightRef.current = true;
        setResendState({ status: 'sending', message: null });
        trackEvent(ANALYTICS_EVENTS.EMAIL_VERIFICATION_RESEND, { platform: 'web', ...context });
        try {
            const data = await resendVerificationEmail();
            if (data?.alreadyVerified) {
                trackEvent(ANALYTICS_EVENTS.EMAIL_VERIFICATION_RESEND_RESULT, { platform: 'web', ...context, result: 'already_verified' });
                setResendState({ status: 'sent', message: 'Your email is already verified.' });
                onAlreadyVerified?.();
                return;
            }
            trackEvent(ANALYTICS_EVENTS.EMAIL_VERIFICATION_RESEND_RESULT, { platform: 'web', ...context, result: 'sent' });
            setResendState({ status: 'sent', message: `Sent! Check ${email || 'your inbox'} — the link is good for 24 hours. Check spam if you don't see it.` });
            setCooldown(Number(data?.resendAvailableInSeconds) || DEFAULT_RESEND_COOLDOWN_SECONDS);
        } catch (err) {
            const rateLimited = err?.code === 'rate_limit_exceeded';
            trackEvent(ANALYTICS_EVENTS.EMAIL_VERIFICATION_RESEND_RESULT, { platform: 'web', ...context, result: rateLimited ? 'rate_limited' : 'failed' });
            if (rateLimited) setCooldown(Number(err?.payload?.retryAfterSeconds) || DEFAULT_RESEND_COOLDOWN_SECONDS);
            setResendState({ status: 'error', message: resendErrorMessage(err) });
        } finally {
            resendInFlightRef.current = false;
        }
    };

    const inbox = <strong className={styles.email}>{email || 'your email address'}</strong>;
    let title = 'Verify your email to start this challenge and earn coins.';
    let body = <>We sent a verification link to {inbox}. Open it while signed in to this account and we'll bring you back to this challenge.</>;
    if (pendingCoin) {
        const coinName = `${rarityName(pendingCoin.rarity)} coin${pendingCoin.mediaTitle ? ` for ${pendingCoin.mediaTitle}` : ''}`;
        title = 'Verify your email to claim your coin';
        body = <>Your {coinName} is waiting. We sent a verification link to {inbox} — verify it and the coin goes straight into your collection, then keep playing.</>;
    } else if (variant === 'keep_playing') {
        title = 'Verify your email to keep playing';
        body = <>We sent a verification link to {inbox}. Verify it to take your next challenge and start earning coins.</>;
    }

    return (
        <div className={styles.card} role="dialog" aria-labelledby="verify-title">
            <span className={styles.icon} aria-hidden="true">📬</span>
            <h1 id="verify-title" className={styles.title}>{title}</h1>
            <p className={styles.body}>{body}</p>
            <div className={styles.actions}>
                <button className={styles.primaryBtn} onClick={onContinue} disabled={continuing}>
                    {continuing ? 'Checking…' : (continueLabel || "I've verified my email")}
                </button>
                <button className={styles.secondaryBtn} onClick={handleResend} disabled={resendState.status === 'sending' || cooldown > 0}>
                    {resendState.status === 'sending' ? 'Sending…' : cooldown > 0 ? `Resend available in ${cooldown}s` : 'Resend verification email'}
                </button>
                {onChangeEmail && (
                    <button className={styles.linkBtn} onClick={onChangeEmail}>Wrong email? Change it</button>
                )}
                {onNotNow && (
                    <button className={styles.linkBtn} onClick={onNotNow}>Not now</button>
                )}
            </div>
            {resendState.message && (
                <p role="status" className={resendState.status === 'error' ? styles.feedbackError : styles.feedbackOk}>{resendState.message}</p>
            )}
            {notice && <p role="status" className={styles.body}>{notice}</p>}
            {error && <p role="alert" className={styles.feedbackError}>{error}</p>}
        </div>
    );
}
